"use client";

import { useState } from "react";
import Link from "next/link";

type FaqItem = { q: string; a: string | React.ReactNode };
type FaqGroup = { label: string; items: FaqItem[] };
type FaqTab = { id: string; emoji: string; label: string; groups: FaqGroup[] };

const TABS: FaqTab[] = [
  {
    id: "candidates",
    emoji: "🎿",
    label: "For Candidates",
    groups: [
      {
        label: "Getting Started",
        items: [
          {
            q: "Is PeakWave free for candidates?",
            a: "Yes, 100% free. Creating a profile, being discovered by employers, and receiving messages is completely free and always will be. We believe seasonaires shouldn't have to pay to find work.",
          },
          {
            q: "How long does it take to create a profile?",
            a: "About 5 minutes. You'll add your personal details, experience, qualifications, availability, and a short bio. You can also upload a 1-minute video intro (optional but highly recommended, employers love them).",
          },
          {
            q: "Can employers see my email and phone number?",
            a: "No. Your contact details are hidden by default. Employers can only reach you through PeakWave's built-in messaging. You choose when and if to share your personal contact information.",
          },
          {
            q: "Do I need to upload a video?",
            a: "It's optional but strongly recommended. Employers consistently tell us that video intros are the most valuable part of a profile. They can see your personality and communication style instantly. Just film a quick 1-minute intro on your phone.",
          },
        ],
      },
      {
        label: "Your Profile",
        items: [
          {
            q: 'What does "Actively Looking" mean?',
            a: 'When you toggle "Actively Looking" on, your profile appears in employer search results. Employers can see when you were last active on the platform (e.g. "Active 2 days ago"), so logging in, updating your profile, or replying to messages keeps you visible. No need to reconfirm anything, just stay active and we handle the rest.',
          },
          {
            q: "Can I apply for both winter and summer roles?",
            a: "Absolutely. You can select both seasons and as many roles and locations as you like. Many seasonaires do a winter in the Alps followed by a summer on the water, and your profile works for both.",
          },
          {
            q: "What happens when I accept an offer?",
            a: "You can update your status at any time between three states: Actively Looking (visible in search), In Conversations (visible but signals you're talking to employers), and Offer Accepted (hidden from default search). If an offer falls through, you can switch back to Actively Looking instantly. Employers can also mark you as hired from their side, which helps us track successful placements.",
          },
          {
            q: "Can I edit my profile after creating it?",
            a: "Yes, anytime. Update your availability, add new qualifications, change your preferred locations, or upload a new video. It's your profile and you can edit anything whenever you like.",
          },
        ],
      },
    ],
  },
  {
    id: "employers",
    emoji: "🏠",
    label: "For Employers",
    groups: [
      {
        label: "Getting Started",
        items: [
          {
            q: "Is PeakWave free for employers?",
            a: "Yes, PeakWave is completely free. You can browse all candidate profiles, use all filters, and message candidates without paying anything. We're focused on growing the community right now.",
          },
          {
            q: "Do I need an account to browse candidates?",
            a: "No, anyone can browse candidate profiles freely. You only need to create a quick employer account (takes 2 minutes) when you want to message a candidate.",
          },
          {
            q: "How do I contact candidates?",
            a: 'Through PeakWave\'s built-in messaging. Click "Message" on any candidate\'s profile, write your message, and they\'ll be notified. All communication stays on the platform, which protects both parties and keeps things organised.',
          },
          {
            q: "What kind of employers is this for?",
            a: "Any business hiring seasonal staff. We built PeakWave with independent operators in mind: chalet owners, boutique hotels, private yachts, surf schools, tennis clubs, festival organisers. But larger companies benefit just as much. No recruitment agency fees, access to a wider pool of candidates, and a faster way to find the right people. Whether you're staffing one chalet or twenty, PeakWave works the same way.",
          },
          {
            q: "Is PeakWave only for small businesses?",
            a: "No. PeakWave is free for businesses of any size. Larger operators save on recruitment agency fees and get access to candidates they wouldn't find through traditional channels. The platform supports team accounts so multiple hiring managers can browse, shortlist, and message candidates together.",
          },
        ],
      },
      {
        label: "Finding the Right People",
        items: [
          {
            q: "What filters can I use to search?",
            a: "You can filter by season, role, location, availability dates, experience level, qualifications, languages, nationality, age range, gender, work eligibility, and whether they have a video intro. This lets you narrow down to exactly the right candidates in seconds.",
          },
          {
            q: "How do I know candidates are genuinely available?",
            a: 'Every candidate card shows when they were last active on the platform (e.g. "Active 2 days ago"). Candidates who haven\'t been active for 90+ days are automatically archived, so you won\'t waste time on ghost profiles.',
          },
        ],
      },
    ],
  },
  {
    id: "general",
    emoji: "ℹ️",
    label: "General",
    groups: [
      {
        label: "About PeakWave",
        items: [
          {
            q: "Who built PeakWave?",
            a: "PeakWave was built by Josh, a former chalet seasonaire who got frustrated with how difficult it was to find seasonal work. After spending weeks clicking through individual resort websites and sending dozens of emails, he decided to build the platform that should have existed.",
          },
          {
            q: "What seasons and roles do you cover?",
            a: "Winter: chalet hosts, chalet chefs, ski instructors, drivers, bar staff, nannies, hotel staff, housekeeping, and more. Summer: yacht crew, surf instructors, kitesurf instructors, wakeboard instructors, tennis coaches, dive instructors, beach club staff, and boat captains. We're always adding more roles based on demand.",
          },
          {
            q: "What locations do you cover?",
            a: "We cover seasonal work destinations worldwide. Snow seasons across the European Alps (France, Switzerland, Austria, Italy), North America (Canada, USA), Japan, Scandinavia, and the Southern Hemisphere (New Zealand, Australia, Chile, Argentina). Sun seasons across the Mediterranean, Caribbean, Southeast Asia, and Australia. Plus festival circuits across the UK, Europe, North America, and Australasia. We're adding new locations all the time as the community grows.",
          },
          {
            q: "How is PeakWave free? What's the catch?",
            a: "There's no catch. PeakWave is genuinely free for both seasonaires and employers. No hidden fees, no premium tiers, no charges to message candidates. We believe seasonal hiring should be accessible to everyone, from independent chalet owners to large resort operators. In the future, we plan to sustain the platform through brand partnerships and advertising from companies relevant to the seasonal community (think outdoor gear, travel insurance, affiliate links). Your data is never sold, and the core platform remains free to use.",
          },
          {
            q: "How can I help PeakWave grow?",
            a: "The single best thing you can do is tell your friends. Share the platform with anyone who's done or is thinking about doing a season. You can also use our referral programme to earn prizes for every friend who signs up.",
          },
        ],
      },
    ],
  },
];

export function FaqAccordion() {
  const [activeTab, setActiveTab] = useState("candidates");
  const [openItem, setOpenItem] = useState<string | null>(null);

  const currentTab = TABS.find((t) => t.id === activeTab)!;

  function toggleItem(key: string) {
    setOpenItem((prev) => (prev === key ? null : key));
  }

  return (
    <>
      {/* Tabs */}
      <div className="flex gap-1 justify-center mx-5 md:mx-auto mb-10 md:mb-12 bg-white rounded-[10px] p-1 w-fit border border-border-light">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setOpenItem(null);
            }}
            className={`px-5 md:px-7 py-2.5 rounded-sm text-[13px] font-semibold cursor-pointer border-none font-sans transition-all ${
              activeTab === tab.id
                ? "bg-navy text-white shadow-[0_2px_8px_rgba(27,42,74,0.2)]"
                : "bg-transparent text-text-muted"
            }`}
          >
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {/* Accordion groups */}
      <div className="max-w-[720px] mx-auto px-5 md:px-12 pb-2">
        {currentTab.groups.map((group) => (
          <div key={group.label} className="mb-9">
            <div className="text-[11px] font-semibold text-gold uppercase tracking-[2px] mb-4 pl-1">
              {group.label}
            </div>
            {group.items.map((item) => {
              const key = `${activeTab}-${item.q}`;
              const isOpen = openItem === key;
              return (
                <div
                  key={item.q}
                  className={`bg-white rounded-md mb-2 border transition-all ${
                    isOpen
                      ? "border-gold/20"
                      : "border-border-light hover:border-gold/20"
                  }`}
                >
                  <button
                    onClick={() => toggleItem(key)}
                    className="w-full flex justify-between items-center px-6 py-5 cursor-pointer bg-transparent border-none font-sans text-left transition-colors hover:bg-gold-pale"
                  >
                    <h3 className="text-[15px] font-semibold flex-1 pr-4">
                      {item.q}
                    </h3>
                    <div
                      className={`w-8 h-8 rounded-sm flex items-center justify-center text-lg shrink-0 transition-all ${
                        isOpen
                          ? "bg-gold text-white rotate-45"
                          : "bg-warm-bg text-text-muted"
                      }`}
                    >
                      +
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? "max-h-[400px]" : "max-h-0"
                    }`}
                  >
                    <div className="px-6 pb-6 text-sm text-text-muted leading-[1.8]">
                      {item.a}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Contact bar */}
      <div className="max-w-[720px] mx-auto px-5 md:px-12 pt-6 pb-12 md:pb-16 text-center">
        <p className="text-[14px] text-text-muted">
          Still have questions? Email us at{" "}
          <Link
            href="mailto:hello@peakwave.co"
            className="text-gold font-medium no-underline hover:underline"
          >
            hello@peakwave.co
          </Link>
        </p>
      </div>
    </>
  );
}
